import { Button, Dialog, Badge } from "@justworkshr/milo-components";
import { Form, Formik } from "formik";
import { addNewDependentInitialFormValues } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { employeeDependentValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import { useContext, useState } from "react";
import styles from "./CurrentEmployees.module.css";
import isEqual from "lodash/isEqual";
import DependentFormComponent from "./DependentForm";
import { AddNewDependentFormType } from "pages/CensusAndCurrentInformation/utils/formTypes";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  useCreateDependentCensusesMutation,
} from "types/generated/operations";
import { ConvertToISODate, errorMessageMap } from "utils/helper";
import { getProspectUuid } from "utils/idHelpers";
import { trackFormError } from "utils/analytics/trackEvent";
import Alert from "uicomponents/Alert";
import { SingleDependentType } from "types";
import { TotalDependentContext } from "./TotalDependentContext";
interface IAddUpsellDependentProps {
  employeeId: string;
  employeeName: string;
  closeParentDialog: () => void;
  updateDependentList: (
    newDependent: SingleDependentType,
    method: "Delete" | "Add" | "Edit"
  ) => void;
}

interface DependentBadgeType {
  firstName: string;
  lastName: string;
  dependentType: string;
}

const AddUpsellDependent = (props: IAddUpsellDependentProps) => {
  const { closeParentDialog, employeeId, updateDependentList, employeeName } =
    props;

  const initialFormValue = addNewDependentInitialFormValues();

  const totalDependentContext = useContext(TotalDependentContext);
  const [isAddDependentDialogOpen, setIsAddDependentDialogOpen] =
    useState(true);
  const [dependentBadgeList, setDependentBadgeList] = useState<
    DependentBadgeType[] | []
  >([]);

  const [createDependentCensuses, { loading: createDependentCensusesLoading }] =
    useCreateDependentCensusesMutation();
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const closeDialog = (newValues: any) => {
    if (isEqual(initialFormValue, newValues)) {
      setIsAddDependentDialogOpen(false);
      closeParentDialog();
    } else {
      setIsExitDialogOpen(true);
      setIsAddDependentDialogOpen(false);
    }
  };

  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const closeExitDialog = () => {
    setIsExitDialogOpen(false);
    closeParentDialog();
  };

  const swapModalAndGoBackToAddingDependents = () => {
    setIsExitDialogOpen(false);
    setIsAddDependentDialogOpen(true);
  };

  const handleOnSubmit = async (
    values: AddNewDependentFormType,
    closeDialog: boolean,
    resetForm: () => void
  ) => {
    try {
      if (isError) {
        setIsError(false);
      }
      const response = await createDependentCensuses({
        variables: {
          prospectId: getProspectUuid(),
          dependentCensuses: [
            {
              employeeId: employeeId,
              gender: values.gender
                .toUpperCase()
                .replace(/-/g, "_") as GenderTypeEnum,
              firstName: values.firstName,
              lastName: values.lastName,
              zip: values.homeZip,
              participantType: values.participantType
                .toUpperCase()
                .replace(/-/g, "_") as ParticipantTypeEnum,
              dateOfBirth: ConvertToISODate(values.dateOfBirth),
            },
          ],
        },
      });

      const newDependent = {
        uuid: response.data?.createDependentCensuses?.uuids?.[0] || "",
        employeeCensusId: employeeId,
        gender: values.gender
          .toUpperCase()
          .replace(/-/g, "_") as GenderTypeEnum,
        firstName: values.firstName,
        lastName: values.lastName,
        homeZip: values.homeZip,
        participantType: values.participantType,
        dateOfBirth: ConvertToISODate(values.dateOfBirth),
      };

      if (totalDependentContext && totalDependentContext.totalDependent < 1) {
        totalDependentContext.setTotalDependent(1);
      }
      updateDependentList(newDependent, "Add");
      if (closeDialog) {
        setIsAddDependentDialogOpen(false);
        closeParentDialog();
      } else {
        setDependentBadgeList([
          ...dependentBadgeList,
          {
            firstName: values.firstName,
            lastName: values.lastName,
            dependentType: values.participantType,
          },
        ]);
        resetForm();
      }
      return;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  return (
    <>
      <div className={styles.addDependentsDialog}>
        <Formik
          initialValues={initialFormValue}
          validationSchema={employeeDependentValidationSchema}
          onSubmit={(values, { resetForm }) =>
            handleOnSubmit(values, true, resetForm)
          }
        >
          {({ values, resetForm, isValid }) => (
            <Dialog
              isOpen={isAddDependentDialogOpen}
              onClose={() => closeDialog(values)}
              title="Add dependents"
              size="xl"
            >
              <Form>
                <DependentFormComponent />
                {isError && <Alert message={errorMessage} />}
                {dependentBadgeList.length > 0 && (
                  <>
                    <div
                      className={styles.badgeTitle}
                    >{`Dependents saved for ${employeeName}`}</div>
                    {dependentBadgeList?.map((eachDependent, index) => (
                      <Badge
                        className={styles.dependentBadge}
                        emphasis="subtle"
                        role="info"
                        key={index}
                        children={`${eachDependent.firstName} ${eachDependent.lastName} (${eachDependent.dependentType})`}
                      />
                    ))}
                  </>
                )}
                <div className={styles.footerButton}>
                  <Button
                    variant="ghost"
                    disabled={!isValid || !values.firstName}
                    onClick={() => handleOnSubmit(values, false, resetForm)}
                  >
                    Save & add new dependent
                  </Button>
                  <Button variant="filled" type="submit">
                    {createDependentCensusesLoading
                      ? "Loading"
                      : "Save & close"}
                  </Button>
                </div>
              </Form>
            </Dialog>
          )}
        </Formik>
      </div>
      <Dialog
        isOpen={isExitDialogOpen}
        onClose={swapModalAndGoBackToAddingDependents}
        data-testid="exit-dialog"
        title="Exit without adding dependents?"
        primaryButton={
          <Button onClick={closeExitDialog} variant="filled">
            Exit
          </Button>
        }
        secondaryButton={
          <Button
            onClick={swapModalAndGoBackToAddingDependents}
            variant="ghost"
          >
            Back
          </Button>
        }
      >
        <p>
          Don’t forget, we need info for all dependents for each employee if
          those dependents are enrolled in your current plan or intend to enroll
          in a new plan.
        </p>
        <p>
          If you leave now, you’ll lose any of the info you’ve already added.
        </p>
      </Dialog>
    </>
  );
};

export default AddUpsellDependent;
